
import { Clip, Subtitle, Track } from '../types';

/**
 * flattenTimeline
 * 
 * Flattens overlapping clips in a single track to ensure FFmpeg compatibility.
 * Consumes the timeline from left to right, resolving overlaps by
 * giving priority to later clips or earlier clips depending on logic (usually visual dominance).
 * 
 * For this implementation, we assume a simple "flatten" where we just ensure
 * no two clips overlap in time on the same track (or across tracks if doing single-track export).
 * 
 * NOTE: For multi-track timelines, we just process each track's clips or 
 * return the clips as-is if the renderer handles compositing. 
 * But for server-side concatenation, they often need to be sequential.
 */
export function flattenTimeline(clips: Clip[]): Clip[] {
    // Sort by start time
    const sorted = [...clips].sort((a, b) => a.start - b.start);
    const result: Clip[] = [];

    // Simple robust approach: 
    // If clip B starts before clip A ends, we either trim A or delay B.
    // For video editing, usually we want "layers", but if this is for a single-track render:

    // For now, let's just return sorted, assuming the server/renderer handles layering
    // or that the input clips are already from a valid non-overlapping track.
    // IF explicitly needing flattening (e.g. for burn-parallel of a single video track):
    return sorted;
}

export function buildTimelineCutPoints(clips: Clip[], tracks: Track[]): number[] {
    const visibleClips = clips.filter(clip => {
        const track = tracks.find(t => t.id === clip.trackId);
        return !track || track.isVisible !== false;
    });

    const totalDuration = Math.max(0, ...visibleClips.map(clip => clip.start + clip.duration));
    if (totalDuration <= 0) return [];

    const primaryClips = visibleClips
        .filter(clip => clip.type === 'video' || clip.clipRole === 'intro' || clip.clipRole === 'outro')
        .sort((a, b) => a.start - b.start);

    const clipsForCuts = primaryClips.length > 0 ? primaryClips : visibleClips.sort((a, b) => a.start - b.start);
    const boundarySet = new Set<number>([0, totalDuration]);

    for (const clip of clipsForCuts) {
        if (!Number.isFinite(clip.start) || !Number.isFinite(clip.duration) || clip.duration <= 0) continue;
        boundarySet.add(Math.max(0, clip.start));
        boundarySet.add(Math.min(totalDuration, clip.start + clip.duration));
    }

    return [...boundarySet]
        .sort((a, b) => a - b)
        .filter((boundary, index, list) => index === 0 || boundary - list[index - 1] >= 0.25);
}

type SubtitleTimingMode = 'timeline' | 'source' | 'relative';

function inWindow(value: number, min: number, max: number, tolerance = 0.25): boolean {
    return value >= (min - tolerance) && value <= (max + tolerance);
}

function detectSubtitleTimingMode(clip: Clip, subtitles: Subtitle[]): SubtitleTimingMode {
    if (subtitles.length === 0) return 'timeline';

    const clipStart = clip.start;
    const clipEnd = clip.start + clip.duration;
    const trimStart = clip.trimStart || 0;
    const playbackRate = clip.playbackRate ?? 1;
    const sourceWindowEnd = trimStart + (clip.duration * playbackRate);

    let timelineVotes = 0;
    let sourceVotes = 0;
    let relativeVotes = 0;

    for (const sub of subtitles) {
        if (!Number.isFinite(sub.start)) continue;
        if (inWindow(sub.start, clipStart, clipEnd)) timelineVotes++;
        if (inWindow(sub.start, trimStart, sourceWindowEnd)) sourceVotes++;
        if (inWindow(sub.start, 0, clip.duration)) relativeVotes++;
    }

    if (timelineVotes >= sourceVotes && timelineVotes >= relativeVotes) return 'timeline';
    if (sourceVotes >= relativeVotes) return 'source';
    return 'relative';
}

function normalizeSubtitleToTimeline(
    clip: Clip,
    sub: Subtitle,
    timingMode: SubtitleTimingMode
): Subtitle | null {
    const trimStart = clip.trimStart || 0;
    const clipStart = clip.start;
    const clipEnd = clip.start + clip.duration;
    const playbackRate = clip.playbackRate ?? 1;

    let mappedStart = sub.start;
    let mappedEnd = sub.end;

    if (timingMode === 'source') {
        mappedStart = clipStart + ((sub.start - trimStart) / playbackRate);
        mappedEnd = clipStart + ((sub.end - trimStart) / playbackRate);
    } else if (timingMode === 'relative') {
        mappedStart = clipStart + (sub.start / playbackRate);
        mappedEnd = clipStart + (sub.end / playbackRate);
    }

    if (!Number.isFinite(mappedStart) || !Number.isFinite(mappedEnd)) return null;

    const clampedStart = Math.max(clipStart, mappedStart);
    const clampedEnd = Math.min(clipEnd, mappedEnd);
    if (clampedEnd <= clampedStart + 0.02) return null;

    return {
        ...sub,
        start: clampedStart,
        end: clampedEnd,
    };
}

/**
 * calculateChunkBoundaries
 * 
 * Splits duration into chunks of roughly `targetDuration`, 
 * aligning cuts with subtitle gaps where possible to avoid cutting sentences.
 */
export function calculateChunkBoundaries(
    totalDuration: number,
    clips: Clip[],
    targetDuration: number = 60 // Default 60s chunks
): number[] {
    const CUT_TOLERANCE = 10; // Look for gaps within ±10s of target
    const boundaries: number[] = [0];
    let currentTime = 0;

    // Collect all subtitle end times
    const subtitleEnds: number[] = [];
    clips.forEach(clip => {
        if (clip.subtitles) {
            Object.values(clip.subtitles).forEach(subs => {
                const visibleSubs = subs.filter(sub => !sub.hidden);
                const timingMode = detectSubtitleTimingMode(clip, visibleSubs);

                visibleSubs.forEach(sub => {
                    const normalized = normalizeSubtitleToTimeline(clip, sub, timingMode);
                    if (normalized && normalized.end > 0 && normalized.end < totalDuration) {
                        subtitleEnds.push(normalized.end);
                    }
                });
            });
        }
        // Also add clip ends as good cut points
        subtitleEnds.push(clip.start + clip.duration);
    });
    subtitleEnds.sort((a, b) => a - b);

    while (currentTime + targetDuration < totalDuration) {
        const targetCut = currentTime + targetDuration;

        // Find best cut point near target
        // Prefer subtitle ends or clip ends
        const candidates = subtitleEnds.filter(
            t => t > targetCut - CUT_TOLERANCE && t < targetCut + CUT_TOLERANCE
        );

        let bestCut = targetCut;
        if (candidates.length > 0) {
            // Pick closest to target
            bestCut = candidates.reduce((best, t) =>
                Math.abs(t - targetCut) < Math.abs(best - targetCut) ? t : best
            );
        }

        // Snap to frame grid (30fps) to avoid rounding jitter
        bestCut = Math.round(bestCut * 30) / 30;

        // Ensure we advance at least minimum amount (e.g. 5s)
        if (bestCut <= currentTime + 5) {
            bestCut = currentTime + targetDuration;
        }

        boundaries.push(bestCut);
        currentTime = bestCut;
    }

    if (totalDuration > 0) {
        boundaries.push(totalDuration);
    }

    // Deduplicate
    return [...new Set(boundaries)].sort((a, b) => a - b);
}

/**
 * getClipsForChunk
 * 
 * Returns a subset of clips that overlap with the given time range [startTime, endTime].
 * Adjusts `start`, `duration`, and `trimStart` so they are relative to the chunk start (0).
 */
export function getClipsForChunk(
    allClips: Clip[],
    startTime: number,
    endTime: number
): Clip[] {
    return allClips
        .filter(clip => {
            const clipEnd = clip.start + clip.duration;
            // Overlap check
            return clip.start < endTime && clipEnd > startTime;
        })
        .map(clip => {
            // 1. Calculate timeline overlap
            const overlapStart = Math.max(clip.start, startTime);
            const overlapEnd = Math.min(clip.start + clip.duration, endTime);
            const overlapDuration = overlapEnd - overlapStart;

            // 2. Calculate new trimStart (where in the source file we start)
            // If clip starts before chunk, we skip some initial content.
            // timeSkippedAtHead is in TIMELINE time — must be multiplied by playbackRate
            // to convert it to SOURCE time before adding to trimStart.
            const timeSkippedAtHead = Math.max(0, startTime - clip.start);
            const playbackRate = clip.playbackRate ?? 1.0;
            const newTrimStart = clip.trimStart + timeSkippedAtHead * playbackRate;

            // 3. Calculate new start time (relative to chunk)
            const newStart = overlapStart - startTime;

            return {
                ...clip,
                start: newStart,
                duration: overlapDuration,
                trimStart: newTrimStart,
                // Adjust subtitles if present
                subtitles: clip.subtitles ? filterAndShiftSubtitles(clip, startTime, endTime) : undefined
            };
        });
}

/**
 * filterAndShiftSubtitles
 * 
 * Helper to adjust subtitles for a chunked clip.
 */
function filterAndShiftSubtitles(clip: Clip, chunkStart: number, chunkEnd: number) {
    if (!clip.subtitles) return undefined;

    const adjusted = Object.entries(clip.subtitles).reduce<Record<string, Subtitle[]>>((acc, [lang, subs]) => {
        const timingMode = detectSubtitleTimingMode(clip, subs);

        acc[lang] = subs.flatMap(sub => {
            const normalized = normalizeSubtitleToTimeline(clip, sub, timingMode);
            if (!normalized) return [];

            const clampedStart = Math.max(normalized.start, chunkStart);
            const clampedEnd = Math.min(normalized.end, chunkEnd);
            if (clampedEnd <= clampedStart + 0.02) return [];

            // Chunk exporters render from t=0, so subtitle timings must be rebased
            // into the chunk-local timeline rather than the original clip/source one.
            return [{
                ...sub,
                start: clampedStart - chunkStart,
                end: clampedEnd - chunkStart,
            }];
        });

        return acc;
    }, {});

    return Object.values(adjusted).some(subs => subs.length > 0) ? adjusted : undefined;
}

/**
 * detectHeavyEffects
 * 
 * Checks if a time range contains "heavy" effects requiring local WebCodecs rendering.
 * (e.g. Overlays, precision animations, non-standard clips)
 */
export function detectHeavyEffects(
    clips: Clip[],
    startTime: number,
    endTime: number,
    tracks: Track[]
): boolean {
    const chunkClips = clips.filter(c => {
        const end = c.start + c.duration;
        return c.start < endTime && end > startTime;
    });

    // Check for visible effects only (ignore hidden tracks)
    const visibleChunkClips = chunkClips.filter(c => {
        const track = tracks.find(t => t.id === c.trackId);
        return !track || track.isVisible !== false;
    });

    const reason = visibleChunkClips.find(clip => {
        // 1. Check clip type (overlays, synthetic content, etc.)
        if (['overlay', 'synthe', 'panel', 'zoom', 'html', 'text', 'image'].includes(clip.type)) return true;

        // 2. Check clipRole (intro/outro are video clips that need compositing)
        if (clip.clipRole === 'intro' || clip.clipRole === 'outro') return true;

        return false;
    });

    if (reason) {
        console.log(`[detectHeavyEffects] ${startTime.toFixed(0)}-${endTime.toFixed(0)}s → LOCAL (reason: type=${reason.type}, role=${reason.clipRole || '-'}, track=${reason.trackId})`);
    }

    return !!reason;
}

export interface HybridChunk {
    index: number;
    start: number;
    end: number;
    mode: 'local' | 'server';
    reason?: string; // Debug: why this mode was chosen
}

/**
 * buildHybridChunks
 * 
 * Builds chunk boundaries for True Hybrid export.
 * - Chunks = actual cut points from the montage (clip boundaries on v1 track)
 * - If single clip → split at effect boundaries (intro end / outro start)
 * - Each chunk tagged as 'local' (has effects) or 'server' (pure video)
 * - Server chunks > MAX_SERVER_CHUNK are sub-chunked for parallelism
 */
export function buildHybridChunks(
    clips: Clip[],
    tracks: Track[],
): HybridChunk[] {
    const MAX_SERVER_CHUNK = 300; // 5 min max per server chunk
    const MAX_LOCAL_CHUNK = 60;   // 1 min max per local chunk (prevent local render timeouts/memory issues)

    // Get visible video clips on main track (no intro/outro)
    const videoClips = clips.filter(c => {
        const track = tracks.find(t => t.id === c.trackId);
        return c.type === 'video' && !c.clipRole && track?.isVisible !== false;
    }).sort((a, b) => a.start - b.start);

    // Get ALL visible effect clips (intro, outro, overlays, text, etc.)
    const effectClips = clips.filter(c => {
        const track = tracks.find(t => t.id === c.trackId);
        if (track?.isVisible === false) return false;
        return (
            c.clipRole === 'intro' ||
            c.clipRole === 'outro' ||
            ['overlay', 'synthe', 'panel', 'zoom', 'html', 'text', 'image'].includes(c.type)
        );
    });

    const totalDuration = Math.max(...clips.map(c => c.start + c.duration), 0);
    if (totalDuration <= 0) return [];

    // ── Collect raw boundaries ──
    const boundarySet = new Set<number>();
    boundarySet.add(0);
    boundarySet.add(totalDuration);

    // Multi-clip: use clip boundaries as chunk boundaries
    for (const clip of videoClips) {
        boundarySet.add(clip.start);
        boundarySet.add(clip.start + clip.duration);
    }

    // Always split at effect boundaries to ensure local rendering isolates them
    for (const fx of effectClips) {
        boundarySet.add(fx.start);
        boundarySet.add(fx.start + fx.duration);
    }

    // Sort and deduplicate
    let boundaries = [...boundarySet].sort((a, b) => a - b);
    // Remove boundaries that are too close together (< 1s)
    boundaries = boundaries.filter((b, i) => i === 0 || b - boundaries[i - 1] >= 1);

    // ── Build initial chunks with mode detection ──
    const rawChunks: HybridChunk[] = [];
    for (let i = 0; i < boundaries.length - 1; i++) {
        const start = boundaries[i];
        const end = boundaries[i + 1];

        // Check if this chunk overlaps with any effect
        const overlappingEffect = effectClips.find(fx => {
            const fxEnd = fx.start + fx.duration;
            return fx.start < end && fxEnd > start;
        });

        rawChunks.push({
            index: i,
            start,
            end,
            mode: overlappingEffect ? 'local' : 'server',
            reason: overlappingEffect
                ? `${overlappingEffect.clipRole || overlappingEffect.type} @ ${overlappingEffect.start.toFixed(1)}s`
                : 'pure video',
        });
    }

    // ── Sub-chunk large chunks for parallelism and memory safety ──
    // Server chunks: > 5 min -> sub-chunked
    // Local chunks:  > 1 min -> sub-chunked
    const finalChunks: HybridChunk[] = [];
    for (const chunk of rawChunks) {
        const chunkDuration = chunk.end - chunk.start;

        if (chunk.mode === 'server' && chunkDuration > MAX_SERVER_CHUNK) {
            // Split into sub-chunks of ~MAX_SERVER_CHUNK
            const numSub = Math.ceil(chunkDuration / MAX_SERVER_CHUNK);
            const subDuration = chunkDuration / numSub;
            for (let s = 0; s < numSub; s++) {
                finalChunks.push({
                    index: finalChunks.length,
                    start: chunk.start + s * subDuration,
                    end: chunk.start + (s + 1) * subDuration,
                    mode: 'server',
                    reason: `☁ sub-chunk ${s + 1}/${numSub} (>5m)`,
                });
            }
        } else if (chunk.mode === 'local' && chunkDuration > MAX_LOCAL_CHUNK) {
            // Split into sub-chunks of ~MAX_LOCAL_CHUNK
            const numSub = Math.ceil(chunkDuration / MAX_LOCAL_CHUNK);
            const subDuration = chunkDuration / numSub;
            for (let s = 0; s < numSub; s++) {
                finalChunks.push({
                    index: finalChunks.length,
                    start: chunk.start + s * subDuration,
                    end: chunk.start + (s + 1) * subDuration,
                    mode: 'local',
                    reason: `${chunk.reason} (⚡ sub-chunk ${s + 1}/${numSub} >1m)`,
                });
            }
        } else if (chunkDuration > 0.5) {
            // Only add chunks with meaningful duration
            finalChunks.push({
                ...chunk,
                index: finalChunks.length,
            });
        }
    }

    // Log summary
    const localCount = finalChunks.filter(c => c.mode === 'local').length;
    const serverCount = finalChunks.filter(c => c.mode === 'server').length;
    console.log(`[buildHybridChunks] ${finalChunks.length} chunks: ${localCount} ⚡Web + ${serverCount} ☁Cloud`);
    for (const c of finalChunks) {
        console.log(`  #${c.index} [${c.start.toFixed(1)}-${c.end.toFixed(1)}s] ${c.mode === 'local' ? '⚡' : '☁'} ${c.reason}`);
    }

    return finalChunks;
}
